import type { ProductCenterProductRow } from "@adpilot/shared";
import { marketplaceCode } from "@/components/layout/shop-scope/model";

/**
 * ASIN filter domain model (Paper "ASIN filter (search-only)"). Users search or
 * paste IDs; every term is resolved against Product Center listings and the
 * matches are grouped into products. The committed filter value is always a
 * list of child ASINs: a parent stands for all of its variations.
 */

export const MAX_ASIN_TERMS = 20;

/** Commas (ASCII and CJK), semicolons and any whitespace, including pasted new lines. */
const TERM_SEPARATOR = /[\s,，;；、]+/;

export interface ParsedTerms {
  terms: string[];
  /** More unique terms were entered than are searched. */
  truncated: boolean;
}

/** Unique terms in entry order; duplicates compare case-insensitively. */
export function parseAsinTerms(input: string): ParsedTerms {
  const seen = new Set<string>();
  const terms: string[] = [];
  for (const raw of input.split(TERM_SEPARATOR)) {
    const term = raw.trim();
    if (!term) continue;
    const key = term.toUpperCase();
    if (seen.has(key)) continue;
    seen.add(key);
    terms.push(term);
  }
  return { terms: terms.slice(0, MAX_ASIN_TERMS), truncated: terms.length > MAX_ASIN_TERMS };
}

/** Pasted lists keep one visible separator style in the single-line field. */
export function normalizePastedTerms(text: string): string {
  return text
    .split(TERM_SEPARATOR)
    .map((term) => term.trim())
    .filter(Boolean)
    .join(", ");
}

export interface AsinListing {
  shopId: string;
  /** Two-letter marketplace code; empty when the shop carries no marketplace. */
  marketplace: string;
  sku: string | null;
  asin: string;
  /** Variation group key; equals `asin` for standalone listings. */
  parentAsin: string | null;
  title: string | null;
  imageUrl: string | null;
}

/** Product Center SKU rows → listings; rows without an ASIN can't be filtered on. */
export function listingsFromProductCenter(rows: readonly ProductCenterProductRow[]): AsinListing[] {
  return rows.flatMap((row) => {
    const { identity, shop } = row;
    if (!identity.asin || !shop) return [];
    return [
      {
        shopId: shop.id,
        marketplace: shop.marketplaceName ? marketplaceCode(shop.marketplaceName) : "",
        sku: identity.sku,
        asin: identity.asin,
        parentAsin: identity.parentAsin,
        title: identity.title,
        imageUrl: identity.imageUrl,
      },
    ];
  });
}

export interface AsinItem {
  asin: string;
  title: string | null;
  imageUrl: string | null;
  skus: string[];
  marketplaces: string[];
  /** Listed in at least one shop of the current scope; otherwise not selectable. */
  inScope: boolean;
}

export interface AsinProduct {
  key: string;
  /** Parent ASIN of a variation family; null for a standalone ASIN. */
  parentAsin: string | null;
  title: string | null;
  imageUrl: string | null;
  marketplaces: string[];
  /** Variations of a family, or the single ASIN of a standalone product. */
  items: AsinItem[];
  inScope: boolean;
}

export interface AsinSearchResults {
  products: AsinProduct[];
  matchedTerms: string[];
  unmatchedTerms: string[];
}

export interface TermListings {
  term: string;
  listings: readonly AsinListing[];
}

const same = (left: string | null, right: string) =>
  left != null && left.toUpperCase() === right.toUpperCase();

function uniquePush<T>(list: T[], value: T | null | undefined) {
  if (value != null && value !== "" && !list.includes(value)) list.push(value);
}

/**
 * Resolves settled term searches into products. Product Center search is a
 * substring match over title/SKU/ASIN/parent, so a term only matches a listing
 * whose ASIN, SKU or parent ASIN equals it (case-insensitive). Variations are
 * grouped under their parent when the parent itself was searched or several of
 * them matched; the same ASIN listed in several marketplaces is one item.
 */
export function buildAsinSearchResults(
  termListings: readonly TermListings[],
  scope: ReadonlySet<string> | null,
): AsinSearchResults {
  const matchedTerms: string[] = [];
  const unmatchedTerms: string[] = [];
  const searchedParents = new Set<string>();
  const listings: AsinListing[] = [];
  const seenListing = new Set<string>();

  for (const { term, listings: candidates } of termListings) {
    const matches = candidates.filter(
      (listing) =>
        same(listing.asin, term) || same(listing.sku, term) || same(listing.parentAsin, term),
    );
    (matches.length > 0 ? matchedTerms : unmatchedTerms).push(term);
    for (const listing of matches) {
      if (
        listing.parentAsin &&
        same(listing.parentAsin, term) &&
        listing.parentAsin !== listing.asin
      ) {
        searchedParents.add(listing.parentAsin);
      }
      const key = `${listing.shopId}\u0000${listing.sku ?? ""}\u0000${listing.asin}`;
      if (seenListing.has(key)) continue;
      seenListing.add(key);
      listings.push(listing);
    }
  }

  const inScope = (listing: AsinListing) => scope === null || scope.has(listing.shopId);
  const items = new Map<string, AsinItem & { parentAsin: string | null }>();
  for (const listing of listings) {
    let item = items.get(listing.asin);
    if (!item) {
      item = {
        asin: listing.asin,
        title: null,
        imageUrl: null,
        skus: [],
        marketplaces: [],
        inScope: false,
        parentAsin: null,
      };
      items.set(listing.asin, item);
    }
    // In-scope listings name the item; another marketplace may carry a different title.
    const preferred = inScope(listing) && !item.inScope;
    if (preferred || item.title == null) item.title = listing.title ?? item.title;
    if (preferred || item.imageUrl == null) item.imageUrl = listing.imageUrl ?? item.imageUrl;
    item.inScope ||= inScope(listing);
    uniquePush(item.skus, listing.sku);
    uniquePush(item.marketplaces, listing.marketplace);
    if (listing.parentAsin && listing.parentAsin !== listing.asin) {
      item.parentAsin ??= listing.parentAsin;
    }
  }

  const childrenByParent = new Map<string, number>();
  for (const item of items.values()) {
    if (item.parentAsin) {
      childrenByParent.set(item.parentAsin, (childrenByParent.get(item.parentAsin) ?? 0) + 1);
    }
  }
  const grouped = (parentAsin: string | null): parentAsin is string =>
    parentAsin != null &&
    (searchedParents.has(parentAsin) || (childrenByParent.get(parentAsin) ?? 0) > 1);

  const products: AsinProduct[] = [];
  const groups = new Map<string, AsinProduct>();
  for (const { parentAsin, ...item } of items.values()) {
    // The parent listing itself is not sellable; it only names its family.
    if (grouped(item.asin)) continue;
    if (!grouped(parentAsin)) {
      products.push({
        key: item.asin,
        parentAsin: null,
        title: item.title,
        imageUrl: item.imageUrl,
        marketplaces: item.marketplaces,
        items: [item],
        inScope: item.inScope,
      });
      continue;
    }
    let group = groups.get(parentAsin);
    if (!group) {
      const parentItem = items.get(parentAsin);
      group = {
        key: `parent:${parentAsin}`,
        parentAsin,
        title: parentItem?.title ?? null,
        imageUrl: parentItem?.imageUrl ?? null,
        marketplaces: [],
        items: [],
        inScope: false,
      };
      groups.set(parentAsin, group);
      products.push(group);
    }
    group.items.push(item);
    group.title ??= item.title;
    group.imageUrl ??= item.imageUrl;
    group.inScope ||= item.inScope;
    for (const code of item.marketplaces) uniquePush(group.marketplaces, code);
  }

  return { products, matchedTerms, unmatchedTerms };
}

/** ASINs a user can select (listed in a shop of the current scope). */
export function selectableAsins(products: readonly AsinProduct[]): string[] {
  return products.flatMap((product) =>
    product.items.filter((item) => item.inScope).map((item) => item.asin),
  );
}

export type AsinCheckState = "checked" | "indeterminate" | "unchecked";

export function productCheckState(
  product: AsinProduct,
  excluded: ReadonlySet<string>,
): AsinCheckState {
  const selectable = product.items.filter((item) => item.inScope);
  if (selectable.length === 0) return "unchecked";
  const selected = selectable.filter((item) => !excluded.has(item.asin)).length;
  if (selected === 0) return "unchecked";
  return selected === selectable.length ? "checked" : "indeterminate";
}

/**
 * Matches come back selected, so the draft records what the user deselected:
 * new results join the selection without an effect, and a deselection survives
 * edits to the search terms.
 */
export function toggleProduct(product: AsinProduct, excluded: ReadonlySet<string>): Set<string> {
  const next = new Set(excluded);
  const on = productCheckState(product, excluded) !== "checked";
  for (const item of product.items) {
    if (!item.inScope) continue;
    if (on) next.delete(item.asin);
    else next.add(item.asin);
  }
  return next;
}

export function toggleAsin(asin: string, excluded: ReadonlySet<string>): Set<string> {
  const next = new Set(excluded);
  if (next.has(asin)) next.delete(asin);
  else next.add(asin);
  return next;
}

export function selectedAsins(
  products: readonly AsinProduct[],
  excluded: ReadonlySet<string>,
): string[] {
  return selectableAsins(products).filter((asin) => !excluded.has(asin));
}

export type AsinSelectionSummary =
  | { kind: "none" }
  | { kind: "products"; products: number }
  | { kind: "withVariations"; products: number; variations: number }
  | { kind: "partialVariations"; products: number; selected: number; total: number };

/** Footer copy model: "1 product selected · includes 3 variations" / "1 product · 2 of 3 variations selected". */
export function summarizeAsinSelection(
  products: readonly AsinProduct[],
  excluded: ReadonlySet<string>,
): AsinSelectionSummary {
  let productCount = 0;
  let selectedVariations = 0;
  let totalVariations = 0;
  for (const product of products) {
    const state = productCheckState(product, excluded);
    if (state === "unchecked") continue;
    productCount += 1;
    if (!product.parentAsin) continue;
    const selectable = product.items.filter((item) => item.inScope);
    totalVariations += selectable.length;
    selectedVariations += selectable.filter((item) => !excluded.has(item.asin)).length;
  }
  if (productCount === 0) return { kind: "none" };
  if (totalVariations === 0) return { kind: "products", products: productCount };
  if (selectedVariations === totalVariations) {
    return { kind: "withVariations", products: productCount, variations: selectedVariations };
  }
  return {
    kind: "partialVariations",
    products: productCount,
    selected: selectedVariations,
    total: totalVariations,
  };
}
